import React, { useState, useEffect } from 'react';
import { string, bool } from 'prop-types';

import {
  params,
  string as stringType,
  shape as shapeType,
  useDataModel
} from '@thd-nucleus/data-sources';
import { useStore } from '@thd-nucleus/experience-context';

import { VEHICLE_CATEGORIES } from '../utils/constants';
import { ToolContext } from '../context/tool-context';
import { useApiData } from '../hooks/useApiData';
import { PriceBar } from './PriceBar/PriceBar';
import { LoadingPriceBar } from './Loading/LoadingPriceBar';
import { DepositCallout } from './DepositCallout/DepositCallout';
import { LoadingDepositCallout } from './Loading/LoadingDepositCallout';
import { StoreCallout } from './StoreCallout/StoreCallout';
import { LoadingStoreCallout } from './Loading/LoadingStoreCallout';
import { PriceError } from './PriceError/PriceError';

import './rental-price.scss';

export const RentalPrice = ({ itemId, hideStoreInfo, hideDepositCallout, depositCalloutReduced }) => {
  const [refresh, setRefresh] = useState(false);

  useEffect(() => {
    LIFE_CYCLE_EVENT_BUS.trigger('rental-price.ready');
  }, []);

  const { data, loading, error } = useDataModel('product', { variables: { itemId } });
  const experienceStore = useStore();

  const toolContextValue = {
    isAVehicle() { return VEHICLE_CATEGORIES.includes(data?.product?.identifiers?.rentalCategory); }
  };
  const isAVehicle = toolContextValue.isAVehicle();

  const { rentalCategory, rentalSubCategory } = data?.product?.identifiers || '';

  const { pricingData, isLoading, isError } = useApiData({
    experienceStore,
    isAVehicle,
    refresh,
    rentalCategory,
    rentalSubCategory,
    setRefresh,
    zipCode: experienceStore.storeZip
  });

  const showStoreInfo = () => {
    return !isLoading && !isError ? <StoreCallout storeInfo={pricingData.closestStoreInfo} /> : <LoadingStoreCallout />;
  };

  const showDepositCallout = () => {
    return (
      <>
        <hr />
        {!isLoading && !isError ? (
          <DepositCallout
            depositAmount={pricingData.closestPricingData?.rentalDeposit?.depositAmount}
            depositCalloutReduced={depositCalloutReduced}
          />
        ) : <LoadingDepositCallout depositCalloutReduced={depositCalloutReduced} />}
      </>
    );
  };

  return (
    <ToolContext.Provider value={toolContextValue}>
      <div className="rental-price">
        {!hideStoreInfo && showStoreInfo()}
        {!isLoading && !isError ? <PriceBar rentalPrices={pricingData.closestPricingData?.rentalPricing} />
          : <LoadingPriceBar />}
        { isError && <PriceError refresh={refresh} setRefresh={setRefresh} />}
        {!hideDepositCallout && showDepositCallout()}
      </div>
    </ToolContext.Provider>
  );
};

RentalPrice.displayName = 'RentalPrice';

RentalPrice.propTypes = {
  depositCalloutReduced: bool,
  hideDepositCallout: bool,
  hideStoreInfo: bool,
  itemId: string.isRequired
};

RentalPrice.defaultProps = {
  depositCalloutReduced: false,
  hideDepositCallout: false,
  hideStoreInfo: false
};

RentalPrice.dataModel = {
  product: params({ itemId: stringType().isRequired(), dataSource: stringType() }).shape({
    itemId: stringType(),
    dataSources: stringType(),
    identifiers: shapeType({
      rentalCategory: stringType(),
      rentalSubCategory: stringType()
    })
  })
};